
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, UserCheck, X } from "lucide-react";
import { CollaboratorPermissions } from "@/types/collaborator";
import { Json } from "@/integrations/supabase/types";

// Helper function to convert Json to CollaboratorPermissions
const jsonToPermissions = (json: Json | null): CollaboratorPermissions => {
  const defaultPermissions: CollaboratorPermissions = {
    posts: true,
    clients: true,
    reports: true,
    calendar: true,
    finances: false,
    workflow: true,
    approvals: true,
    automation: true
  };

  if (!json || typeof json !== 'object') return defaultPermissions;
  
  const jsonObj = json as Record<string, any>;
  return {
    posts: Boolean(jsonObj.posts ?? defaultPermissions.posts),
    clients: Boolean(jsonObj.clients ?? defaultPermissions.clients),
    reports: Boolean(jsonObj.reports ?? defaultPermissions.reports),
    calendar: Boolean(jsonObj.calendar ?? defaultPermissions.calendar),
    finances: Boolean(jsonObj.finances ?? defaultPermissions.finances),
    workflow: Boolean(jsonObj.workflow ?? defaultPermissions.workflow),
    approvals: Boolean(jsonObj.approvals ?? defaultPermissions.approvals),
    automation: Boolean(jsonObj.automation ?? defaultPermissions.automation)
  };
};

interface InviteData {
  id: string;
  company_id: string;
  email: string;
  permissions: CollaboratorPermissions;
  token: string;
  status: string;
  expires_at: string;
  created_at: string;
  owner_name?: string;
  agency_name?: string;
}

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [isValidating, setIsValidating] = useState(true);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      toast.error("Link de convite inválido");
      navigate("/");
      return;
    }

    const validateInvite = async () => {
      try {
        setIsValidating(true);
        
        // Get current user
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          toast.error("Você precisa estar logado para aceitar convites");
          navigate("/login");
          return;
        }

        // Check if token is valid
        const { data: inviteData, error } = await supabase
          .from("collaborator_invites")
          .select("*")
          .eq("token", token)
          .eq("status", "pending")
          .gt("expires_at", new Date().toISOString())
          .single();

        if (error || !inviteData) {
          toast.error("Convite não encontrado, expirado ou já aceito");
          navigate("/");
          return;
        }

        // Check if the invite is for the current user's email
        if (inviteData.email !== session.user.email) {
          toast.error("Este convite não é para o seu email");
          navigate("/");
          return;
        }

        // Get company details - usando a tabela companies através do company_id
        const { data: companyData } = await supabase
          .from("companies")
          .select("name, agency_name")
          .eq("id", inviteData.company_id)
          .single();

        setInvite({
          ...inviteData,
          permissions: jsonToPermissions(inviteData.permissions),
          owner_name: companyData?.name || "Empresa",
          agency_name: companyData?.agency_name || "Empresa"
        });
        
      } catch (error: any) {
        console.error("Error validating invite:", error);
        toast.error("Erro ao validar convite");
        navigate("/");
      } finally {
        setIsValidating(false);
      }
    };

    validateInvite();
  }, [token, navigate]);

  const handleAcceptInvite = async () => {
    if (!invite) return;
    
    try {
      setIsLoading(true);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Usuário não autenticado");
        return;
      }

      console.log('Accepting invite using edge function for user:', session.user.id, 'company:', invite.company_id);

      // Use the edge function to accept the invite
      const { data, error } = await supabase.functions.invoke('accept-collaborator-invite', {
        body: { token: invite.token },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      console.log('Edge function response:', data);

      if (data?.success) {
        toast.success("Convite aceito com sucesso! Você agora é um colaborador.");
        
        // Wait a bit before redirecting to ensure all changes are processed
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      } else {
        throw new Error(data?.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error("Error accepting invite:", error);
      toast.error(error.message || "Erro ao aceitar convite");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeclineInvite = async () => {
    if (!invite) return;
    
    try {
      setIsLoading(true);
      
      // Update invite status to declined
      const { error } = await supabase
        .from("collaborator_invites")
        .update({ status: 'declined' })
        .eq("token", invite.token);

      if (error) throw error;

      toast.success("Convite recusado");
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Error declining invite:", error);
      toast.error("Erro ao recusar convite");
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-[400px] shadow-md">
          <CardHeader className="text-center">
            <CardTitle>Validando convite...</CardTitle>
            <CardDescription>Aguarde enquanto validamos seu convite.</CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="w-[400px] shadow-md">
          <CardHeader className="text-center">
            <CardTitle>Convite inválido</CardTitle>
            <CardDescription>Este convite não é válido ou já expirou.</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => navigate("/")} className="w-full">
              Ir para a página inicial
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const renderPermissionsList = () => {
    const activePermissions = Object.entries(invite.permissions)
      .filter(([_, value]) => value)
      .map(([key, _]) => {
        const labels: Record<string, string> = {
          posts: 'Posts',
          clients: 'Clientes',
          reports: 'Relatórios',
          calendar: 'Calendário',
          finances: 'Financeiro',
          workflow: 'Fluxo de Trabalho',
          approvals: 'Aprovações',
          automation: 'Automação'
        };
        return labels[key] || key;
      });

    return activePermissions.join(', ');
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-[500px] shadow-md">
        <CardHeader className="text-center">
          <CardTitle>Convite para Colaboração</CardTitle>
          <CardDescription>
            <strong>{invite?.owner_name}</strong> da empresa <strong>{invite?.agency_name}</strong> 
            convidou você para colaborar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2">Você terá acesso a:</h4>
            <p className="text-blue-800 text-sm">{invite ? renderPermissionsList() : ''}</p>
          </div>
          
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <h4 className="font-medium text-red-900 mb-2">Restrições:</h4>
            <p className="text-red-800 text-sm">
              Os dados financeiros são sempre privados e não serão compartilhados.
            </p>
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">
              Convite válido até: {invite ? new Date(invite.expires_at).toLocaleDateString() : ''}
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleDeclineInvite} 
            disabled={isLoading}
            className="flex-1"
          >
            <X className="h-4 w-4 mr-2" />
            Recusar
          </Button>
          <Button 
            onClick={handleAcceptInvite} 
            disabled={isLoading}
            className="flex-1"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Aceitando...
              </>
            ) : (
              <>
                <UserCheck className="mr-2 h-4 w-4" />
                Aceitar
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
